import { useEffect, useState } from "react";

export default function TaskListSettingsModal({
  currentTitle,
  tasksVisibleOnHome,
  timerEnabled,
  timerPaused,
  timerMinutes,
  timerStartedAt,
  timerPausedAt,
  onSaveTitle,
  onToggleTasks,
  onToggleTimer,
  onPauseTimer,
  onResumeTimer,
  onResetTimer,
  onResetProgress,
  onClose,
}) {
  const [title, setTitle] = useState(currentTitle);
  const [minutes, setMinutes] = useState(String(timerMinutes || 10));
  const [clockNow, setClockNow] = useState(Date.now());

  useEffect(() => setTitle(currentTitle), [currentTitle]);
  useEffect(() => setMinutes(String(timerMinutes || 10)), [timerMinutes]);
  useEffect(() => {
    if (!timerEnabled || timerPaused) return undefined;
    const interval = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [timerEnabled, timerPaused]);

  function validMinutes() {
    const value = Number.parseInt(minutes, 10);
    return Number.isFinite(value) ? Math.min(180, Math.max(1, value)) : 10;
  }

  function saveTitle(event) {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (trimmedTitle) onSaveTitle(trimmedTitle);
  }

  const durationSeconds = timerMinutes * 60;
  const referenceTime = timerPausedAt ? Date.parse(timerPausedAt) : clockNow;
  const elapsedSeconds = timerStartedAt
    ? Math.floor((referenceTime - Date.parse(timerStartedAt)) / 1000)
    : 0;
  const remainingSeconds = Math.min(durationSeconds, Math.max(0, durationSeconds - elapsedSeconds));
  const remainingDisplay = `${String(Math.floor(remainingSeconds / 60)).padStart(2, "0")}:${String(remainingSeconds % 60).padStart(2, "0")}`;
  const expectedStopTime = timerStartedAt
    ? new Date(Date.parse(timerStartedAt) + durationSeconds * 1000)
    : null;

  return (
    <div className="ct-modal-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="ct-modal ct-settings-modal" role="dialog" aria-modal="true" aria-labelledby="task-list-settings-title">
        <div className="ct-modal-header">
          <div>
            <p className="ct-eyebrow">Leerkracht dashboard</p>
            <h2 id="task-list-settings-title" className="ct-modal-title">Instellingen takenlijst</h2>
          </div>
          <button type="button" className="ct-close-button" onClick={onClose} aria-label="Sluiten">×</button>
        </div>

        <div className="ct-settings-content">
          <section className="ct-settings-section">
            <h3 className="ct-settings-heading">Takenoverzicht</h3>
            <form className="ct-settings-title-form" onSubmit={saveTitle}>
              <label className="ct-form-label" htmlFor="task-list-title">Titel van de takenlijst</label>
              <div className="ct-settings-title-row">
                <input
                  id="task-list-title"
                  className="ct-form-input"
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  maxLength={80}
                />
                <button className="ct-button-primary" type="submit">Opslaan</button>
              </div>
            </form>
            <label className="ct-settings-checkbox">
              <input
                type="checkbox"
                checked={tasksVisibleOnHome}
                onChange={(event) => onToggleTasks(event.target.checked)}
              />
              <span>Toon taken in het klasoverzicht</span>
            </label>
          </section>

          <section className="ct-settings-section">
            <h3 className="ct-settings-heading">Klassikale timer</h3>
            <p className="ct-panel-subtitle">De aftelling verschijnt naast de klasvoortgang op het leerlingenscherm.</p>
            {timerEnabled && (
              <div className={`ct-timer-live-status${timerPaused ? " is-paused" : ""}${remainingSeconds === 0 ? " is-expired" : ""}`}>
                <span className="ct-timer-live-value">
                  <span aria-hidden="true">{timerPaused ? "⏸" : "⏱"}</span>
                  {timerPaused ? "Gepauzeerd" : remainingSeconds === 0 ? "Timer afgelopen" : "Timer loopt"}
                  <strong>{remainingDisplay}</strong>
                </span>
                {!timerPaused && remainingSeconds > 0 && expectedStopTime && (
                  <span className="ct-timer-stop-time">
                    Stopt om {expectedStopTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            )}
            <label className="ct-settings-checkbox">
              <input
                type="checkbox"
                checked={timerEnabled}
                onChange={(event) => onToggleTimer(event.target.checked, validMinutes())}
              />
              <span>Timer inschakelen</span>
            </label>
            <div className="ct-settings-title-row ct-settings-timer-row">
              <label className="ct-form-label" htmlFor="class-timer-minutes">Duur in minuten</label>
              <input
                id="class-timer-minutes"
                className="ct-form-input ct-timer-minutes"
                type="number"
                min="1"
                max="180"
                step="1"
                value={minutes}
                onChange={(event) => setMinutes(event.target.value)}
              />
              {timerEnabled ? (
                <>
                  <button
                    className="ct-button-secondary"
                    type="button"
                    onClick={timerPaused ? onResumeTimer : onPauseTimer}
                  >
                    {timerPaused ? "Hervatten" : "Pauzeren"}
                  </button>
                  <button className="ct-button-secondary" type="button" onClick={() => onResetTimer(validMinutes())}>
                    Timer resetten
                  </button>
                </>
              ) : (
                <button className="ct-button-primary" type="button" onClick={() => onToggleTimer(true, validMinutes())}>
                  Timer starten
                </button>
              )}
            </div>
            <p className="ct-settings-note">Kies een duur van 1 tot 180 minuten.</p>
          </section>

          <section className="ct-settings-section ct-settings-danger-section">
            <h3 className="ct-settings-heading">Voortgang</h3>
            <p className="ct-panel-subtitle">Verwijder alle afgevinkte taken voor deze klas.</p>
            <button className="ct-button-danger" type="button" onClick={onResetProgress}>
              Voortgang resetten
            </button>
          </section>
        </div>

        <div className="ct-settings-footer">
          <button className="ct-button-secondary" type="button" onClick={onClose}>Sluiten</button>
        </div>
      </section>
    </div>
  );
}
